// ADDITIONAL DETAIL (owner, 2026-09-25 — D-154): *"The Marketing Category
// field does not justify its own row. Let's treat it like we treat Associated
// Entities."*
//
// The icon to the LEFT of a line's Associated Entities icon, and the pop-up it
// opens. It replaces the Marketing and Transportation sub-rows that used to sit
// under a qualifying line, in both tables:
//  - a MARKETING line (its account is a Marketing account) shows the
//    Marketing Category;
//  - a TRANSPORTATION line (its company is `PACE Healthcare Transportation`,
//    D-190) shows the Transportation Category and the Fleet Maintenance
//    checkbox (D-200);
//  - a line that is both shows both.
// THE ICON SHOWS ON EVERY LINE, WHEREVER IT SITS (owner, 2026-09-28). On a line
// that owes nothing it stays grey and the pop-up says so in one sentence. It
// lights up blue only when the line owes a category and has it, and wears the
// red border when a category it owes is empty and the viewer can fill it in.
// Which sections a line gets, and when anything inside is red, come from
// `../lib/required.ts`. The icon and the pop-up frame are `LineItemPopover`,
// shared with Associated Entities.
//
// EDITABLE AT IMPORT CONTROL (D-190; there since AP/AR Approval merged into
// it, D-216), and the Marketing Category wherever the Account is (D-192).
// Anywhere else a field the viewer cannot edit is PLAIN TEXT. The locked-box
// look it had at Import Control while that step touched the Company alone
// (D-174) went with that rule. A hold changes nothing here: a held invoice's
// fields stay editable (owner, 2026-10-03 — D-282).
//
// FLEET MAINTENANCE IS A CHECKBOX WITH THREE LOOKS (owner, 2026-09-29 —
// D-200), because it must be ANSWERED and a plain checkbox cannot show "not
// answered". Unanswered (`null`) it shows the dash of a mixed checkbox, the
// red required border where the viewer can answer it, and the words "Not
// answered yet". A click answers Yes (ticked); the next click answers No
// (unticked); after that it toggles. Where it is read-only it is the word Yes,
// No or Not answered. It is editable exactly where the Transportation Category
// is: it goes with the category section, and uses that section's grant.
//
// The fields do not save themselves. The caller passes its own save wiring —
// the editor's detail debounce and mutex, or the read-only table's one-pick
// write — so this file owns the look and nothing about persistence.
import { useEffect, useRef, useState } from 'react'
import { type AdditionalDetailSections } from '@ap/shared'
import { Caption1, Checkbox, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { ClipboardTaskListLtrFilled, ClipboardTaskListLtrRegular } from '@fluentui/react-icons'
import { searchMarketingCategories, searchTransportationCategories } from '../lib/data'
import { FLEET_COMPANY_NAME } from '../lib/constants'
import {
  fleetMaintenanceText,
  hasAdditionalDetail,
  nextFleetMaintenance,
  type AdditionalDetailState,
} from '../lib/required'
import type { MarketingCategory } from '../lib/types'
import { useLineItemPanelStyles, useRequiredStyles } from '../lib/theme'
import { EntityPicker, type EntityOption } from './EntityPicker'
import { LineItemPopover } from './LineItemIconButton'

/** The name on the icon and the heading in the pop-up. */
export const ADDITIONAL_DETAIL_LABEL = 'Additional Detail'

/** What the pop-up says on a line that owes nothing. */
export const NOTHING_OWED_NOTE =
  `Nothing else is needed for this line. A Marketing Category is needed on a line coded to a Marketing ` +
  `Account, and a Transportation Category and Fleet Maintenance on a ${FLEET_COMPANY_NAME} line.`

/** The checkbox's label (D-200). */
export const FLEET_MAINTENANCE_LABEL = 'Fleet Maintenance'

/** What the checkbox says while nobody has answered it. */
const FLEET_MAINTENANCE_UNANSWERED = 'Not answered yet'

const useStyles = makeStyles({
  // Label, then value, one pair per row, labels in one column.
  grid: {
    display: 'grid',
    gridTemplateColumns: '170px minmax(0, 1fr)',
    alignItems: 'center',
    columnGap: '10px',
    rowGap: '6px',
  },
  label: { fontSize: '12px', color: tokens.colorNeutralForeground2, whiteSpace: 'nowrap' },
  text: { fontSize: tokens.fontSizeBase300, minWidth: 0, overflowWrap: 'anywhere' },
})

/** Fleet Maintenance (D-200): its stored answer, and the click wiring. */
interface FleetMaintenanceSection {
  value: boolean | null
  /** Present = editable here. Absent = plain text. */
  onChange?: (value: boolean) => void
  saving?: boolean
}

/** One category section — Marketing and Transportation have the same shape (D-190). */
interface CategorySection {
  /** The picked category's id, and its "code — context" text (`detailCategoryLabel`). */
  categoryId: string | null
  label: string
  /** Present = editable here. Absent = plain text. */
  onSelect?: (option: EntityOption) => void
  /** A write in flight for this line. */
  saving?: boolean
  /** Remount key, so a refused pick re-seeds from the stored value. */
  pickerKey?: string
  /** The refusal message's id while this category's write is refused (C1-09). */
  describedBy?: string
}

interface Props {
  sections: AdditionalDetailSections
  state: AdditionalDetailState
  marketing?: CategorySection
  transportation?: CategorySection
  /** Shown with the Transportation section, and editable where it is (D-200). */
  fleetMaintenance?: FleetMaintenanceSection
  /** A sentence in place of the fields — an unsaved line has nothing to write to yet. */
  hint?: string
  mountNode?: HTMLElement | null
  /** The icon shows but does not open — the line-item bulk row's Apply mode,
      which writes no Additional Detail (D-242). */
  disabled?: boolean
}

export function AdditionalDetailPopover({ sections, state, marketing, transportation, fleetMaintenance, hint, mountNode, disabled }: Props) {
  return (
    <LineItemPopover
      regularIcon={ClipboardTaskListLtrRegular}
      filledIcon={ClipboardTaskListLtrFilled}
      label={ADDITIONAL_DETAIL_LABEL}
      filled={state.filled}
      requiredMissing={state.requiredMissing}
      mountNode={mountNode}
      disabled={disabled}
    >
      <AdditionalDetailPanel
        sections={sections}
        state={state}
        marketing={marketing}
        transportation={transportation}
        fleetMaintenance={fleetMaintenance}
        hint={hint}
      />
    </LineItemPopover>
  )
}

function AdditionalDetailPanel({ sections, state, marketing, transportation, fleetMaintenance, hint }: Omit<Props, 'mountNode' | 'disabled'>) {
  const ps = useLineItemPanelStyles()
  const s = useStyles()
  const rs = useRequiredStyles()
  // Listboxes mount INSIDE the pop-up, so picking an option is not an outside
  // click that dismisses it — the Associated Entities editor's own fix.
  const rootRef = useRef<HTMLDivElement>(null)
  const [popupHost, setPopupHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setPopupHost(rootRef.current)
  }, [])

  const owed = hasAdditionalDetail(sections)
  const live = !hint
  // A value nobody may change here: plain text.
  const readValue = (_label: string, value: string) => <span className={s.text}>{value}</span>
  const row = (
    label: string,
    section: CategorySection,
    invalid: boolean,
    search: (text: string) => Promise<readonly Pick<MarketingCategory, 'id' | 'category' | 'context'>[]>,
  ) => (
    <>
      <span className={s.label}>{label}</span>
      {live && section.onSelect !== undefined ? (
        <EntityPicker
          key={section.pickerKey}
          size="small"
          ariaLabel={label}
          mountNode={popupHost}
          invalid={invalid}
          // Busy, not disabled, while it saves: focus stays put (C2-06).
          busy={section.saving}
          describedBy={section.describedBy}
          value={{ id: section.categoryId ?? undefined, name: section.categoryId ? section.label : undefined }}
          search={(t) =>
            search(t).then<EntityOption[]>((cs) => cs.map((c) => ({ id: c.id, primary: c.category, secondary: c.context })))
          }
          onSelect={(o) => section.onSelect?.(o)}
        />
      ) : (
        readValue(label, section.label)
      )}
    </>
  )

  return (
    <div className={ps.root} ref={rootRef}>
      <div className={ps.head}>
        <span className={ps.headLabel}>{ADDITIONAL_DETAIL_LABEL}</span>
      </div>
      {!owed && <Caption1 className={ps.note}>{NOTHING_OWED_NOTE}</Caption1>}
      {owed && hint && <Caption1 className={ps.note}>{hint}</Caption1>}
      {owed && !hint && (
        <div className={s.grid}>
          {sections.marketing &&
            marketing &&
            row('Marketing Category', marketing, state.marketingInvalid, searchMarketingCategories)}
          {sections.transportation &&
            transportation &&
            row('Transportation Category', transportation, state.transportationInvalid, searchTransportationCategories)}
          {sections.transportation && fleetMaintenance && (
            <>
              <span className={s.label}>{FLEET_MAINTENANCE_LABEL}</span>
              {live && fleetMaintenance.onChange !== undefined ? (
                <Checkbox
                  aria-label={FLEET_MAINTENANCE_LABEL}
                  className={mergeClasses(state.fleetMaintenanceInvalid && rs.requiredIcon)}
                  checked={fleetMaintenance.value === null ? 'mixed' : fleetMaintenance.value}
                  // Not disabled while it saves, which would throw focus to
                  // the page (C2-06): a press during the save is ignored.
                  aria-busy={fleetMaintenance.saving || undefined}
                  label={fleetMaintenance.value === null ? FLEET_MAINTENANCE_UNANSWERED : undefined}
                  onChange={() => {
                    if (fleetMaintenance.saving) return
                    fleetMaintenance.onChange?.(nextFleetMaintenance(fleetMaintenance.value))
                  }}
                />
              ) : (
                readValue(FLEET_MAINTENANCE_LABEL, fleetMaintenanceText(fleetMaintenance.value))
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
